import { AlertCircle, ArrowLeftRight, Loader2, RefreshCw } from "lucide-react";
import { useId } from "react";

import { formatSendMoneyWithCode } from "@/modules/dashboard/dispersions/components/WalletSend/formatSendMoney";
import { QuoteValidity } from "@/modules/dashboard/dispersions/components/WalletSend/QuoteValidity";
import type { NativeQuote } from "@/modules/dashboard/dispersions/services/paymentsQuotes.service";
import { currencySymbol } from "@/modules/dashboard/dispersions/utils/currencyDisplay";
import { formatRateLine } from "@/modules/dashboard/dispersions/utils/walletSend";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import {
  AmountBreakdownList,
  type AmountBreakdownRow,
} from "@/shared/components/payments/AmountBreakdownList";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { maskAmount, type MaskedAmount, maskForCurrency } from "@/shared/utils/amountMask";
import { compareDecimalStrings } from "@/shared/utils/decimal";

import { type SwapAnchor, swapBreakdown, swapReadableRate } from "../../utils/walletSwap";

export interface SwapAmountFieldsProps {
  originCurrency: string;
  destinationCurrency: string;
  anchor: SwapAnchor;
  /** What each field shows: the typed one, and the other one as the quote answered it. */
  send: MaskedAmount;
  receive: MaskedAmount;
  onSendChange: (next: MaskedAmount) => void;
  onReceiveChange: (next: MaskedAmount) => void;
  /** The price on screen: the current one, or the last for this pair while the next arrives. */
  quote: NativeQuote | null;
  loading: boolean;
  /** Said on the "you convert" field: the balance. */
  sendError: string | null;
  quoteError: string | null;
  onRetry: () => void;
}

function AmountInput({
  label,
  currency,
  value,
  onChange,
  derived,
  loading,
  error,
  autoFocus,
}: {
  label: string;
  currency: string;
  value: MaskedAmount;
  onChange: (next: MaskedAmount) => void;
  /** Filled in by the quote, not typed: dimmed while the next price arrives. */
  derived: boolean;
  loading: boolean;
  error: string | null;
  autoFocus?: boolean;
}) {
  const id = useId();
  const hintId = useId();
  const mask = maskForCurrency(currency);
  const symbol = currencySymbol(currency);
  return (
    <div className="space-y-1.5">
      <label
        htmlFor={id}
        className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground"
      >
        {label}
        {derived && loading && <Loader2 className="size-3 animate-spin" aria-hidden="true" />}
      </label>
      <div
        className={cn(
          "rounded-xl border-2 bg-card px-4 py-3 transition-colors",
          error
            ? "border-destructive shadow-[0_0_0_4px_color-mix(in_oklch,var(--destructive)_12%,transparent)]"
            : derived
              ? "border-border"
              : "border-primary shadow-[0_0_0_4px_color-mix(in_oklch,var(--primary)_12%,transparent)]",
        )}
      >
        <div
          className={cn("flex items-baseline gap-2 transition-opacity", derived && loading && "opacity-50")}
        >
          {symbol && (
            <span aria-hidden="true" className="font-mono text-2xl font-bold text-muted-foreground">
              {symbol}
            </span>
          )}
          <input
            id={id}
            autoFocus={autoFocus}
            value={value.display}
            onChange={(event) => onChange(maskAmount(event.target.value, mask))}
            inputMode={mask.decimalSep ? "decimal" : "numeric"}
            placeholder="0"
            autoComplete="off"
            aria-invalid={!!error}
            aria-describedby={error ? hintId : undefined}
            className="w-full min-w-0 bg-transparent font-mono text-3xl font-bold tracking-tight text-foreground outline-none placeholder:text-muted-foreground/40"
          />
          <span className="shrink-0 font-mono text-sm font-medium tracking-wider text-muted-foreground">
            {currency}
          </span>
        </div>
      </div>
      {error && (
        <p id={hintId} role="alert" className="text-sm font-medium text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * The two linked amounts of a conversion. Whichever was typed last is what the price fixes; the other is the
 * quote's answer. The rate sits between them, the way the money crosses from one to the other.
 */
export function SwapAmountFields({
  originCurrency,
  destinationCurrency,
  anchor,
  send,
  receive,
  onSendChange,
  onReceiveChange,
  quote,
  loading,
  sendError,
  quoteError,
  onRetry,
}: SwapAmountFieldsProps) {
  const { t, activeLanguage } = useTranslations("swaps");
  const typed = anchor === "send" ? send : receive;
  const shown = typed.value == null ? null : quote;
  const breakdown = shown ? swapBreakdown(shown) : null;
  const rate = shown ? swapReadableRate(shown) : null;
  const inOrigin = (value: string) => formatSendMoneyWithCode(value, originCurrency, activeLanguage);
  const inDestination = (value: string) =>
    formatSendMoneyWithCode(value, destinationCurrency, activeLanguage);
  const free = !!breakdown && compareDecimalStrings(breakdown.fee, "0") <= 0;

  const rows: AmountBreakdownRow[] = breakdown
    ? [
        {
          key: "fee",
          label: t("convert_row_fee"),
          value: free ? t("convert_fee_free") : inOrigin(breakdown.fee),
        },
        { key: "debit", label: t("convert_row_debit"), value: inOrigin(breakdown.debit) },
        {
          key: "credit",
          label: t("convert_row_credit"),
          value: inDestination(breakdown.credit),
          total: true,
        },
      ]
    : [];

  return (
    <section className="space-y-3">
      <AmountInput
        autoFocus
        label={t("convert_send_label")}
        currency={originCurrency}
        value={send}
        onChange={onSendChange}
        derived={anchor === "receive"}
        loading={loading}
        error={sendError}
      />
      <div className="flex min-h-9 flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-lg bg-muted/50 px-3 py-2 text-xs">
        <span className="flex items-center gap-1.5 text-muted-foreground">
          <ArrowLeftRight className="size-3.5 shrink-0" aria-hidden="true" />
          {t("convert_rate")}
          {rate && (
            <span className="font-mono font-semibold text-foreground tabular-nums">
              {formatRateLine(rate, activeLanguage)}
            </span>
          )}
        </span>
        {shown && <QuoteValidity expiresAt={shown.expires_at} />}
      </div>
      <AmountInput
        label={t("convert_receive_label")}
        currency={destinationCurrency}
        value={receive}
        onChange={onReceiveChange}
        derived={anchor === "send"}
        loading={loading}
        error={null}
      />
      {quoteError ? (
        <ColocaAlert
          variant="destructive"
          icon={AlertCircle}
          text={quoteError}
          actions={
            <ColocaButton size="sm" variant="default" icon={RefreshCw} onClick={onRetry}>
              {t("convert_retry")}
            </ColocaButton>
          }
        />
      ) : breakdown ? (
        <AmountBreakdownList rows={rows} loading={loading} />
      ) : null}
    </section>
  );
}
